import React, { useState } from 'react';
import { cn, getFontFamily } from '@/lib/utils';
import { Eye, EyeOff } from 'lucide-react';
import { useWebinarT } from '@/hooks/useWebinarT';

export function InputLabel({
  label,
  id: idProp,
  type = 'text',
  disabled = false,
  readOnly = false,
  value: externalValue,
  onChange: externalOnChange,
  onKeyDown: externalOnKeyDown,
  theme,
  fontFamily,
  classNameLabel,
  classNameInput,
  className,
  min,
  max,
  maxLength,
  autoComplete,
  inputMode,
  placeholder,
  multiline = false,
  rows = 4,
  hidePasswordToggle = false,
}: {
  label: string;
  id?: string;
  type?:
    | 'text'
    | 'email'
    | 'password'
    | 'number'
    | 'tel'
    | 'url'
    | 'date'
    | 'time'
    | 'datetime-local'
    | 'month'
    | 'week';
  disabled?: boolean;
  readOnly?: boolean;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  theme?: 'LIGHT' | 'DARK';
  fontFamily?: string;
  classNameLabel?: string;
  classNameInput?: string;
  className?: string;
  min?: number;
  max?: number;
  maxLength?: number;
  autoComplete?: string;
  inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  hidePasswordToggle?: boolean;
}) {
  const { t } = useWebinarT('common');
  const autoId = React.useId();
  const id = idProp || autoId;
  const [isFocused, setIsFocused] = useState(false);
  const [internalValue, setInternalValue] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // const inputRef = React.useRef<HTMLInputElement>(null);

  const value = externalValue !== undefined ? externalValue : internalValue;
  const isControlled = externalValue !== undefined;

  // Placeholder fourni : on garde le label flotté pour éviter qu'il chevauche le placeholder.
  const isActive = isFocused || value.length > 0 || Boolean(placeholder);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;

    if (maxLength && e.target.value.length > maxLength) {
      e.target.value = e.target.value.slice(0, maxLength);
    }

    // Une valeur initiale absente (undefined) ne doit pas couper la saisie du formulaire parent.
    if (!isControlled) setInternalValue(e.target.value);
    externalOnChange?.(e);
  };

  // useEffect(() => {
  //   if (inputRef.current && !isControlled) {
  //     setInternalValue(inputRef.current.value || '');
  //   }
  // }, []);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const inputType = type === 'password' ? (showPassword ? 'text' : 'password') : type;

  return (
    <div
      className={cn(
        'relative px-4 py-2 rounded-lg border border-input',
        multiline ? 'h-auto' : 'h-14',
        disabled
          ? 'bg-subtle cursor-not-allowed border-none'
          : theme === 'LIGHT'
            ? 'bg-gray-100/30'
            : theme === 'DARK'
              ? 'bg-white/10'
              : 'bg-white',
        className,
      )}
    >
      <label
        className={cn(
          `absolute transition-all duration-200 pointer-events-none text-tertiary ${
            isActive ? 'text-sm top-0.5 left-3 px-1' : 'text-base  top-1/2 left-4 -translate-y-1/2'
          } ${disabled && 'text-disable'}`,
          classNameLabel,
        )}
        htmlFor={id}
        style={{
          fontFamily: getFontFamily(fontFamily),
        }}
      >
        {label}
      </label>

      {multiline ? (
        <textarea
          className={cn(
            'w-full text-base focus:outline-none focus:ring-0 focus:border-transparent pt-4 resize-y bg-transparent',
            disabled ? 'text-disable cursor-not-allowed' : readOnly ? 'cursor-default' : '',
            !disabled &&
              (theme === 'LIGHT' ? 'text-black' : theme === 'DARK' ? 'text-white' : 'text-black'),
            classNameInput,
          )}
          id={id}
          rows={rows}
          value={value}
          onChange={(e) => {
            if (readOnly) return;

            const next = maxLength ? e.target.value.slice(0, maxLength) : e.target.value;
            if (!isControlled) setInternalValue(next);
            // externalOnChange est typé input ; seul .target.value est lu côté appelant.
            externalOnChange?.({
              ...e,
              target: { ...e.target, value: next },
            } as unknown as React.ChangeEvent<HTMLInputElement>);
          }}
          onFocus={() => !disabled && setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          placeholder={placeholder}
        />
      ) : (
        <input
          className={cn(
            'w-full text-base focus:outline-none focus:ring-0 focus:border-transparent pt-4',
            disabled ? 'text-disable cursor-not-allowed' : readOnly ? 'cursor-default' : '',
            !disabled &&
              (theme === 'LIGHT' ? 'text-black' : theme === 'DARK' ? 'text-white' : 'text-black'),
            classNameInput,
          )}
          id={id}
          type={inputType}
          value={value}
          onChange={handleChange}
          onKeyDown={externalOnKeyDown}
          onFocus={() => !disabled && setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          readOnly={readOnly}
          min={min}
          max={max}
          maxLength={maxLength}
          placeholder={placeholder}
          autoComplete={autoComplete}
          inputMode={inputMode}
        />
      )}

      {/* Password visibility toggle button */}
      {type === 'password' && value.length > 0 && !hidePasswordToggle && (
        <button
          aria-label={t('showPassword')}
          title={t('showPassword')}
          type="button"
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer "
          onClick={togglePasswordVisibility}
          disabled={disabled}
        >
          {showPassword ? <EyeOff className="size-6" /> : <Eye className="size-6" />}
        </button>
      )}
    </div>
  );
}
